import { useEffect } from 'react';
import { MuiDialog } from '../@material-extend';
import { Box, Typography, Paper, TextField, Button } from '@mui/material';
import Constant from '../../util/constants';

// User defined functions and components
import { StyledImg } from '../common/Styled';
import { getGoogleMapUrl, getFBShareUrl } from '../../../src/util/Display';
import { useDomainConfig } from '../../hooks/useDomainConfig';

// Dialog Images
import FacebookIcon from '../../image/social-icons/facebook-logo.svg';
import TwitterIcon from '../../image/social-icons/twitter-logo.svg';
import EmailIcon from '../../image/social-icons/gmail.png';
import DefaultLocationImg from '../../image/default_geocode-2x.png'

const { label, action } = Constant;

const styles  = {
    parentImgBox: {
        display: 'flex',
        alignItems: 'center',
        padding: '16px',
        backgroundColor: 'rgba(145, 158, 171, 0.08)',
        borderRadius: '8px',
        marginBottom: '16px',
    },
    titleParentBox: {
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: '16px',
        flex: 1,
        overflow: 'hidden'
    },
    parentLinkBox: {
        marginTop: '16px',
        padding: '16px',
        border: '1px solid rgba(145, 158, 171, 0.24)',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    imgBox: {
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 8px 0 rgba(145, 158, 171, 0.16)'
    },
    copyLinkBtn: {
        whiteSpace: 'nowrap',
        fontWeight: 600
    },
    social: {
        display: 'flex',
        padding: '24px 0 8px 0',
        justifyContent: 'center',
        gap: '32px',
        '& .social-icon': {
            width: 48,
            height: 48,
            minWidth: 48,
            minHeight: 48,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '8px',
            backgroundColor: 'rgba(145, 158, 171, 0.08)',
            borderRadius: '12px',
            transition: 'all 0.2s ease-in-out',
        },
        '& .social-action': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '8px',
            minWidth: '64px',
            borderRadius: '12px',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            textDecoration: 'none',
            '&:hover': {
                backgroundColor: 'rgba(145, 158, 171, 0.04)',
            },
            '&:hover .social-icon': {
                transform: 'scale(1.1)',
                backgroundColor: 'rgba(145, 158, 171, 0.16)',
            }
        }
    }
}

export default function ShareDialog({ location, map, ...props}) {
    const domainConfig = useDomainConfig();

    const handleClick = (type) => async () => {
        switch(type) {
            case action.ON_COPY_CLICK:
                const linkText = document.getElementById('link');
                if(linkText) {
                    linkText.select();
                    const selection = document.getSelection();
                    const selectionInString = selection.toString();
                    if('clipboard' in navigator) {
                        await navigator.clipboard.writeText(selectionInString);
                    }
                }
                break;
            case action.ON_FB_SHARE_CLICK:
                window.fbShareWindowRef = window.open(getFBShareUrl(getGoogleMapUrl(location.address)), "_blank")
                break;
        }
    }

    return (
        <MuiDialog
        maxWidth='sm'
        fullWidth={!0}
        actions={false} 
        title={label.SHARE}
        className='cm-share-dialog'
        sx={{ '& .MuiDialog-paper': { borderRadius: '16px', p: 1 } }}
        {...props}
        >
            <Box className="cm-share-dialog--inner" sx={{ px: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary" mb={2}>
                    {label.SEND_LINK}
                </Typography>
                
                <Paper component="div" sx={styles.parentImgBox} elevation={0}>
                    <Box aria-hidden="true" sx={styles.imgBox}>
                        <StyledImg src={DefaultLocationImg} width={32} height={32} />
                    </Box>
                    <Box sx={styles.titleParentBox}>
                        <Typography variant="subtitle2" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden" color="text.primary">
                            {location.name}
                        </Typography>
                        <Typography variant="body2" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden" color="text.secondary">
                            {location.address || "Charger Location"}
                        </Typography>
                    </Box>
                </Paper>

                <Paper className="relative" component="div" sx={styles.parentLinkBox} elevation={0}>
                    <TextField 
                        id="link" 
                        size="small"
                        defaultValue={getGoogleMapUrl(location.address)} 
                        inputProps={{ readOnly: !0 }} 
                        variant="outlined"
                        fullWidth
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: 'rgba(145, 158, 171, 0.04)' } }}
                    />
                    <Button 
                        variant="contained" 
                        id="copy-text" 
                        onClick={handleClick(action.ON_COPY_CLICK)} 
                        sx={{ ...styles.copyLinkBtn, borderRadius: '8px', boxShadow: 'none' }}
                        disableElevation
                    >
                        Copy
                    </Button>
                </Paper>
            </Box>

            <Box className="cm-share-dialog--social" sx={styles.social}>
                <Box className='fb-share-button'>
                    <Box component="button" className="social-action" onClick={handleClick(action.ON_FB_SHARE_CLICK)}>
                        <Box aria-hidden="true" className="social-icon">
                            <StyledImg src={FacebookIcon} width={28} height={28} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>Facebook</Typography>
                    </Box>
                </Box>
                <Box component="button" className="social-action" onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(getGoogleMapUrl(location.address))}&text=Check out this ${domainConfig.title} station!`, "_blank")}>
                    <Box aria-hidden="true" className="social-icon">
                        <StyledImg src={TwitterIcon} width={28} height={28} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>Twitter</Typography>
                </Box>
                <Box component="button" className="social-action" onClick={() => window.open(`mailto:?subject=${domainConfig.title} Station Location&body=Check out this EV charging station on ${domainConfig.title}: ${getGoogleMapUrl(location.address)}`, "_blank")}>
                    <Box aria-hidden="true" className="social-icon">
                        <StyledImg src={EmailIcon} width={28} height={28} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>Email</Typography>
                </Box>
            </Box>
        </MuiDialog>
    )
}